import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import CrmLayout from "../crmLayout/CrmLayout";
import usePageTitle from "../utils/usePageTitle";

// Stilizare exact ca în template-ul WowDash (UnitCountTwo / LatestPerformanceOne):
// carduri gradient cu iconiță rotundă 48px, valori h6, liste cu avataruri și text mic.

// Stat card în stilul UnitCountTwo — fără sparkline (regulă: fără grafice decorative)
function StatCard({ titlu, valoare, extra, icon, culoare, gradient, onClick }) {
  return (
    <div className="col-xxl-4 col-sm-6">
      <div
        className={`card p-3 shadow-2 radius-8 border input-form-light h-100 ${gradient}`}
        style={{ cursor: "pointer" }}
        onClick={onClick}
      >
        <div className="card-body p-0">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-8">
            <div className="d-flex align-items-center gap-2">
              <span className={`mb-0 w-48-px h-48-px ${culoare} flex-shrink-0 text-white d-flex justify-content-center align-items-center rounded-circle h6`}>
                <Icon icon={icon} className="icon" />
              </span>
              <div>
                <span className="mb-2 fw-medium text-secondary-light text-sm">{titlu}</span>
                <h6 className="fw-semibold mb-0">{valoare}</h6>
              </div>
            </div>
          </div>
          {extra && <p className="text-sm mb-0">{extra}</p>}
        </div>
      </div>
    </div>
  );
}

// Card-listă în stilul LatestPerformanceOne: header cu „Vezi toate” + listă compactă
function ListaCard({ titlu, subtitlu, items, emptyText, onClickAll, onItemClick, renderItem }) {
  return (
    <div className="col-xxl-6">
      <div className="card h-100 radius-8 border-0">
        <div className="card-header border-bottom bg-base py-12 px-24 d-flex align-items-center justify-content-between">
          <div>
            <h6 className="mb-0 fw-bold text-lg">{titlu}</h6>
            {subtitlu && <span className="text-sm fw-medium text-secondary-light">{subtitlu}</span>}
          </div>
          <button
            type="button"
            className="btn btn-sm btn-link text-primary-600 hover-text-primary d-flex align-items-center gap-1 p-0"
            onClick={onClickAll}
          >
            Vezi toate
            <Icon icon="solar:alt-arrow-right-linear" className="icon" />
          </button>
        </div>
        <div className="card-body p-24">
          {items.length === 0 && <p className="text-secondary-light text-sm mb-0">{emptyText}</p>}
          {items.map((item, i) => (
            <div
              key={item.id ?? i}
              className="d-flex align-items-center justify-content-between gap-2 py-8 border-bottom border-neutral-100"
              style={{ cursor: onItemClick ? "pointer" : "default" }}
              onClick={() => onItemClick?.(item)}
            >
              {renderItem(item)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Avatar circular cu inițială — ca în template
function Avatar({ text, culoare }) {
  return (
    <span className={`w-40-px h-40-px ${culoare} text-white d-flex justify-content-center align-items-center rounded-circle flex-shrink-0 fw-semibold text-sm`}>
      {text.slice(0, 1).toUpperCase()}
    </span>
  );
}

export default function DashboardPage() {
  usePageTitle("Dashboard");
  const { user, hasPerm, hasAnyPerm } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [eroare, setEroare] = useState("");

  useEffect(() => {
    api.get("/api/dashboard").then(setD).catch((err) => setEroare(err.message));
  }, []);

  const eAdmin = hasPerm("finance.read");
  const eCoordonator = hasPerm("lead.read");
  const eProgramator = hasPerm("report.own") && !hasPerm("report.read");
  const eSeo = hasAnyPerm(["seo.read", "seo.read.assigned"]) && !hasPerm("lead.read");

  const G = ["bg-gradient-end-1", "bg-gradient-end-2", "bg-gradient-end-3", "bg-gradient-end-4", "bg-gradient-end-5", "bg-gradient-end-6"];

  return (
    <CrmLayout>
      <div className="d-flex align-items-center gap-2 mb-16">
        <h6 className="mb-0 fw-semibold text-xl">Bună, {user?.nume}!</h6>
        <span className="text-secondary-light text-sm">— {user?.role}</span>
      </div>

      {eroare && <div className="alert alert-danger">{eroare}</div>}
      {!d && !eroare && (
        <div className="text-center py-64">
          <div className="spinner-border text-primary-600" role="status"><span className="visually-hidden">Se încarcă…</span></div>
        </div>
      )}

      {d && (
        <section className="row gy-4">
          {/* === Stat carduri — admin/coordonator === */}
          {(eAdmin || eCoordonator) && (
            <>
              <StatCard titlu="Leaduri de sunat" valoare={d.leaduriDeSunat.length} gradient={G[0]}
                icon="mingcute:phone-call-fill" culoare="bg-primary-600"
                extra={<span><span className="bg-primary-50 px-1 rounded-2 fw-medium text-primary-600 text-sm">status DE_SUNAT</span> <span className="text-secondary-light text-sm">acum</span></span>}
                onClick={() => navigate("/leaduri?status=DE_SUNAT")} />
              <StatCard titlu="Fără contact 7 zile" valoare={d.faraContact7Zile.length} gradient={G[3]}
                icon="mingcute:alarm-fill" culoare="bg-danger-main"
                extra={<span className="text-secondary-light text-sm">după regula din plan</span>}
                onClick={() => navigate("/leaduri?faraContact7Zile=1")} />
              <StatCard titlu="Activități azi" valoare={d.activitatiAzi} gradient={G[1]}
                icon="mingcute:calendar-fill" culoare="bg-success-main"
                extra={<span className="text-secondary-light text-sm">în calendarul de activități</span>}
                onClick={() => navigate("/activitati")} />
              <StatCard titlu="Activități întârziate" valoare={d.activitatiIntarziate} gradient={G[3]}
                icon="mingcute:alarm-2-fill" culoare="bg-danger-main"
                extra={<span className="text-secondary-light text-sm">programate în trecut</span>}
                onClick={() => navigate("/activitati")} />
              <StatCard titlu="Site-uri active" valoare={d.siteuriActive} gradient={G[4]}
                icon="mdi:web" culoare="bg-purple"
                extra={<span className="text-secondary-light text-sm">proiecte în derulare</span>}
                onClick={() => navigate("/proiecte")} />
              <StatCard titlu="Termen depășit" valoare={d.siteuriTermenDepasit} gradient={G[3]}
                icon="mingcute:time-fill" culoare="bg-danger-main"
                extra={<span className="text-secondary-light text-sm">site-uri cu termenul expirat</span>}
                onClick={() => navigate("/proiecte")} />
              <StatCard titlu="Rapoarte SEO scadente" valoare={d.rapoarteSeoScadente.length} gradient={G[2]}
                icon="mingcute:search-3-fill" culoare="bg-yellow"
                extra={<span className="text-secondary-light text-sm">azi sau depășite</span>}
                onClick={() => navigate("/seo/rapoarte-azi")} />
              <StatCard titlu="Raportări de joi lipsă" valoare={d.raportariLipsa?.length ?? 0} gradient={G[5]}
                icon="mingcute:document-fill" culoare="bg-cyan"
                extra={<span className="text-secondary-light text-sm">săptămâna curentă</span>}
                onClick={() => navigate("/raportare")} />
            </>
          )}

          {/* === Admin: finanțe === */}
          {eAdmin && d.solduriSiPrognoza && (
            <>
              <StatCard titlu="Sold actual" valoare={`${d.solduriSiPrognoza.soldActual} MDL`} gradient={G[1]}
                icon="streamline:bag-dollar-solid" culoare="bg-success-main"
                extra={<span className="text-secondary-light text-sm">toate conturile</span>}
                onClick={() => navigate("/finante/analiza")} />
              <StatCard titlu="Intrări preconizate" valoare={`${d.solduriSiPrognoza.intrariPreconizate30Zile} MDL`} gradient={G[0]}
                icon="mingcute:arrow-up-fill" culoare="bg-primary-600"
                extra={<span className="text-secondary-light text-sm">următoarele 30 de zile</span>}
                onClick={() => navigate("/finante/planificare")} />
              <StatCard titlu="Ieșiri programate" valoare={`${d.solduriSiPrognoza.iesiriProgramate30Zile} MDL`} gradient={G[3]}
                icon="mingcute:arrow-down-fill" culoare="bg-danger-main"
                extra={<span className="text-secondary-light text-sm">următoarele 30 de zile</span>}
                onClick={() => navigate("/finante/planificare")} />
              <StatCard titlu="Sold estimat" valoare={`${d.solduriSiPrognoza.soldEstimat} MDL`} gradient={G[2]}
                icon="mingcute:chart-bar-fill" culoare="bg-yellow"
                extra={<span className="text-secondary-light text-sm">conturi + prognoză</span>}
                onClick={() => navigate("/finante/analiza")} />
            </>
          )}

          {/* === Programator === */}
          {eProgramator && (
            <>
              <StatCard titlu="Site-urile mele" valoare={d.siteuriAtribuite?.length ?? 0} gradient={G[0]}
                icon="mdi:web" culoare="bg-primary-600"
                extra={<span className="text-secondary-light text-sm">proiecte atribuite</span>}
                onClick={() => navigate("/proiecte")} />
              <StatCard titlu="Raportarea mea" gradient={d.raportareProprie === "FINAL" ? G[1] : G[2]}
                valoare={d.raportareProprie === null ? "Neîncepută" : d.raportareProprie === "DRAFT" ? "Draft" : "Finalizată"}
                icon="mingcute:document-fill"
                culoare={d.raportareProprie === "FINAL" ? "bg-success-main" : "bg-yellow"}
                extra={<span className="text-secondary-light text-sm">săptămâna curentă</span>}
                onClick={() => navigate("/raportare")} />
            </>
          )}

          {/* === SEO === */}
          {eSeo && (
            <>
              <StatCard titlu="Proiectele mele SEO" valoare={d.proiecteSeoAtribuite?.length ?? 0} gradient={G[2]}
                icon="mingcute:search-3-fill" culoare="bg-yellow"
                extra={<span className="text-secondary-light text-sm">proiecte atribuite</span>}
                onClick={() => navigate("/seo/proiecte")} />
              <StatCard titlu="Calendar SEO azi" valoare={d.calendarSeoAzi?.length ?? 0} gradient={G[1]}
                icon="mingcute:calendar-fill" culoare="bg-success-main"
                extra={<span className="text-secondary-light text-sm">intrări planificate</span>}
                onClick={() => navigate("/seo/calendar")} />
              <StatCard titlu="Rapoarte scadente" valoare={d.proiecteSeoAtribuite?.filter((p) => p.scadent).length ?? 0} gradient={G[3]}
                icon="mingcute:alarm-fill" culoare="bg-danger-main"
                extra={<span className="text-secondary-light text-sm">azi sau depășite</span>}
                onClick={() => navigate("/seo/rapoarte-azi")} />
            </>
          )}

          {/* === Liste compacte === */}
          {(eAdmin || eCoordonator) && (
            <>
              <ListaCard
                titlu="Leaduri de sunat"
                subtitlu="primii 8, cei mai vechi"
                items={d.leaduriDeSunat}
                emptyText="Niciun lead de sunat."
                onClickAll={() => navigate("/leaduri?status=DE_SUNAT")}
                onItemClick={(l) => navigate(`/leaduri?lead=${l.id}`)}
                renderItem={(l) => (
                  <>
                    <div className="d-flex align-items-center gap-2">
                      <Avatar text={l.nume} culoare="bg-primary-600" />
                      <div>
                        <span className="text-md d-block line-height-1 fw-medium text-primary-light">{l.nume}</span>
                        <span className="text-sm d-block fw-normal text-secondary-light">{l.telefon}</span>
                      </div>
                    </div>
                    <span className="text-sm fw-medium text-secondary-light">{l.responsabil}</span>
                  </>
                )}
              />
              <ListaCard
                titlu="Fără contact de 7 zile"
                subtitlu="trebuie sunați din nou"
                items={d.faraContact7Zile}
                emptyText="Toate leadurile au contact recent."
                onClickAll={() => navigate("/leaduri?faraContact7Zile=1")}
                onItemClick={(l) => navigate(`/leaduri?lead=${l.id}`)}
                renderItem={(l) => (
                  <>
                    <div className="d-flex align-items-center gap-2">
                      <Avatar text={l.nume} culoare="bg-danger-main" />
                      <div>
                        <span className="text-md d-block line-height-1 fw-medium text-primary-light">{l.nume}</span>
                        <span className="text-sm d-block fw-normal text-secondary-light">{l.telefon}</span>
                      </div>
                    </div>
                    <span className="bg-danger-50 text-danger-600 px-8 py-4 radius-8 text-sm fw-medium">7+ zile</span>
                  </>
                )}
              />
              <ListaCard
                titlu="Rapoarte SEO scadente"
                subtitlu="azi și depășite"
                items={d.rapoarteSeoScadente}
                emptyText="Nicio raportare scadentă."
                onClickAll={() => navigate("/seo/rapoarte-azi")}
                renderItem={(s) => (
                  <>
                    <div className="d-flex align-items-center gap-2">
                      <Avatar text={s.site} culoare="bg-yellow" />
                      <div>
                        <span className="text-md d-block line-height-1 fw-medium text-primary-light">{s.site}</span>
                        <span className="text-sm d-block fw-normal text-secondary-light">{s.responsabil}</span>
                      </div>
                    </div>
                    <span className="bg-warning-50 text-warning-600 px-8 py-4 radius-8 text-sm fw-medium">scadent</span>
                  </>
                )}
              />
              <ListaCard
                titlu="Raportări de joi lipsă"
                subtitlu="săptămâna curentă"
                items={(d.raportariLipsa ?? []).map((nume) => ({ nume }))}
                emptyText="Toată echipa a transmis raportul."
                onClickAll={() => navigate("/raportare")}
                renderItem={(r) => (
                  <>
                    <div className="d-flex align-items-center gap-2">
                      <Avatar text={r.nume} culoare="bg-danger-main" />
                      <span className="text-md fw-medium text-primary-light">{r.nume}</span>
                    </div>
                    <span className="text-sm fw-medium text-secondary-light">netransmis</span>
                  </>
                )}
              />
            </>
          )}

          {eAdmin && d.solduriSiPrognoza && (
            <div className="col-xxl-6">
              <div className="card h-100 radius-8 border-0">
                <div className="card-header border-bottom bg-base py-12 px-24 d-flex align-items-center justify-content-between">
                  <div>
                    <h6 className="mb-0 fw-bold text-lg">Solduri și prognoză</h6>
                    <span className="text-sm fw-medium text-secondary-light">cash-flow pe 30 de zile</span>
                  </div>
                  <button type="button" className="btn btn-sm btn-link text-primary-600 hover-text-primary d-flex align-items-center gap-1 p-0"
                    onClick={() => navigate("/finante/analiza")}>
                    Analiză
                    <Icon icon="solar:alt-arrow-right-linear" className="icon" />
                  </button>
                </div>
                <div className="card-body p-24">
                  <ul className="mb-0 list-unstyled">
                    {[
                      { label: "Sold actual (toate conturile)", valoare: d.solduriSiPrognoza.soldActual, cls: "text-success-main" },
                      { label: "Intrări preconizate (30 zile)", valoare: d.solduriSiPrognoza.intrariPreconizate30Zile, cls: "text-primary-600" },
                      { label: "Ieșiri programate (30 zile)", valoare: d.solduriSiPrognoza.iesiriProgramate30Zile, cls: "text-danger-main" },
                      { label: "Sold estimat", valoare: d.solduriSiPrognoza.soldEstimat, cls: "text-warning-main" },
                    ].map((r) => (
                      <li key={r.label} className="d-flex align-items-center justify-content-between py-8 border-bottom border-neutral-100">
                        <span className="text-sm fw-medium text-secondary-light">{r.label}</span>
                        <span className={`text-sm fw-semibold ${r.cls}`}>{r.valoare} MDL</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {eProgramator && (
            <div className="col-xxl-12">
              <div className="card h-100 radius-8 border-0">
                <div className="card-header border-bottom bg-base py-12 px-24 d-flex align-items-center justify-content-between">
                  <div>
                    <h6 className="mb-0 fw-bold text-lg">Site-urile mele</h6>
                    <span className="text-sm fw-medium text-secondary-light">proiecte și etape atribuite</span>
                  </div>
                  <button type="button" className="btn btn-sm btn-link text-primary-600 hover-text-primary d-flex align-items-center gap-1 p-0"
                    onClick={() => navigate("/proiecte")}>
                    Vezi toate
                    <Icon icon="solar:alt-arrow-right-linear" className="icon" />
                  </button>
                </div>
                <div className="card-body p-24">
                  <div className="table-responsive scroll-sm">
                    <table className="table bordered-table mb-0">
                      <thead>
                        <tr>
                          <th scope="col">Site</th>
                          <th scope="col">Status</th>
                          <th scope="col">Termen</th>
                          <th scope="col">Etapele mele</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.siteuriAtribuite?.map((s) => (
                          <tr key={s.id} style={{ cursor: "pointer" }} onClick={() => navigate(`/proiecte/${s.id}`)}>
                            <td><span className="text-md d-block line-height-1 fw-medium text-primary-light">{s.numeSite}</span></td>
                            <td>
                              <span className={`px-12 py-6 radius-8 text-sm fw-medium ${s.status === "FINALIZAT" ? "bg-success-50 text-success-600" : s.status === "IN_LUCRU" ? "bg-warning-50 text-warning-600" : "bg-neutral-100 text-secondary-light"}`}>
                                {s.status === "NEINCEPUT" ? "Neînceput" : s.status === "IN_LUCRU" ? "În lucru" : "Finalizat"}
                              </span>
                            </td>
                            <td>
                              {s.termen ? (
                                <span className={`text-sm ${s.intarziat ? "text-danger-main fw-medium" : "text-secondary-light"}`}>
                                  {new Date(s.termen).toLocaleDateString("ro-RO")}{s.intarziat ? " · depășit" : ""}
                                </span>
                              ) : (
                                <span className="text-sm text-secondary-light">—</span>
                              )}
                            </td>
                            <td>
                              {s.etapeleMele?.map((e) => (
                                <span key={e.id} className="text-sm d-block text-secondary-light">{e.nume} · {e.status === "FINALIZAT" ? "finalizată" : e.status === "IN_LUCRU" ? "în lucru" : "neîncepută"}</span>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {eSeo && (
            <div className="col-xxl-12">
              <div className="card h-100 radius-8 border-0">
                <div className="card-header border-bottom bg-base py-12 px-24 d-flex align-items-center justify-content-between">
                  <div>
                    <h6 className="mb-0 fw-bold text-lg">Proiectele mele SEO</h6>
                    <span className="text-sm fw-medium text-secondary-light">raportări scadente evidențiate</span>
                  </div>
                  <button type="button" className="btn btn-sm btn-link text-primary-600 hover-text-primary d-flex align-items-center gap-1 p-0"
                    onClick={() => navigate("/seo/proiecte")}>
                    Vezi toate
                    <Icon icon="solar:alt-arrow-right-linear" className="icon" />
                  </button>
                </div>
                <div className="card-body p-24">
                  {d.proiecteSeoAtribuite?.map((p) => (
                    <div key={p.id} className="d-flex align-items-center justify-content-between gap-2 py-8 border-bottom border-neutral-100"
                      style={{ cursor: "pointer" }} onClick={() => navigate(`/seo/proiecte`)}>
                      <div className="d-flex align-items-center gap-2">
                        <Avatar text={p.site} culoare="bg-yellow" />
                        <div>
                          <span className="text-md d-block line-height-1 fw-medium text-primary-light">{p.site}</span>
                          <span className="text-sm d-block fw-normal text-secondary-light">
                            {p.nextReportDate ? `raportare: ${new Date(p.nextReportDate).toLocaleDateString("ro-RO")}` : "fără dată de raportare"}
                          </span>
                        </div>
                      </div>
                      {p.scadent ? (
                        <span className="bg-danger-50 text-danger-600 px-8 py-4 radius-8 text-sm fw-medium">scadent</span>
                      ) : (
                        <span className="bg-success-50 text-success-600 px-8 py-4 radius-8 text-sm fw-medium">în termen</span>
                      )}
                    </div>
                  ))}
                  {d.proiecteSeoAtribuite?.length === 0 && (
                    <p className="text-secondary-light text-sm mb-0">Niciun proiect atribuit.</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </section>
      )}
    </CrmLayout>
  );
}
